import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createProjectRole,
  createProjectPermissionRequest,
  deactivateClientOrganizationMember,
  deleteProjectMembership,
  deleteProjectRole,
  getClientOrganizationMembers,
  getClientProjectMembersForAdmin,
  getClientProjectsForAdmin,
  getMyAdminProjects,
  getProjectAdministrators,
  getProjectMemberships,
  getProjectPermissions,
  getProjectPermissionRequests,
  getProjectRoles,
  getProjectUsers,
  updateProjectAdministrators,
  updateProjectMembership,
  updateProjectRole,
  upsertProjectMembership,
  reviewProjectPermissionRequest,
} from '../api/api';
import type {
  AdministratorsUpdateInput,
  MembershipUpdateInput,
  MembershipInput,
  PermissionRequestCreateInput,
  PermissionRequestReviewInput,
  RoleInput,
} from '../types';

export const rolesPermissionsKeys = {
  all: ['roles-permissions'] as const,
  clientAdminProjects: () => [...rolesPermissionsKeys.all, 'client-admin-projects'] as const,
  clientProjectMembers: () => [...rolesPermissionsKeys.all, 'client-project-members'] as const,
  clientOrganizationMembers: () =>
    [...rolesPermissionsKeys.all, 'client-organization-members'] as const,
  myAdminProjects: () => [...rolesPermissionsKeys.all, 'my-admin-projects'] as const,
  projectPermissions: (projectId: number) =>
    [...rolesPermissionsKeys.all, 'project-permissions', projectId] as const,
  projectRoles: (projectId: number) =>
    [...rolesPermissionsKeys.all, 'project-roles', projectId] as const,
  projectMemberships: (projectId: number) =>
    [...rolesPermissionsKeys.all, 'project-memberships', projectId] as const,
  projectUsers: (projectId: number) =>
    [...rolesPermissionsKeys.all, 'project-users', projectId] as const,
  projectAdministrators: (projectId: number) =>
    [...rolesPermissionsKeys.all, 'project-administrators', projectId] as const,
  projectPermissionRequests: (projectId: number, status?: string) =>
    [...rolesPermissionsKeys.all, 'project-permission-requests', projectId, status ?? 'all'] as const,
};

export const useClientAdminProjects = () =>
  useQuery({
    queryKey: rolesPermissionsKeys.clientAdminProjects(),
    queryFn: getClientProjectsForAdmin,
    staleTime: 30_000,
  });

export const useClientProjectMembers = () =>
  useQuery({
    queryKey: rolesPermissionsKeys.clientProjectMembers(),
    queryFn: getClientProjectMembersForAdmin,
    staleTime: 30_000,
  });

export const useClientOrganizationMembers = () =>
  useQuery({
    queryKey: rolesPermissionsKeys.clientOrganizationMembers(),
    queryFn: getClientOrganizationMembers,
    staleTime: 30_000,
  });

export const useMyAdminProjects = (enabled: boolean = true) =>
  useQuery({
    queryKey: rolesPermissionsKeys.myAdminProjects(),
    queryFn: getMyAdminProjects,
    staleTime: 60_000,
    enabled,
  });

export const useProjectPermissions = (projectId: number | undefined) =>
  useQuery({
    queryKey: rolesPermissionsKeys.projectPermissions(projectId!),
    queryFn: () => getProjectPermissions(projectId!),
    enabled: Boolean(projectId),
    staleTime: 60_000,
  });

export const useProjectRoles = (projectId: number | undefined) =>
  useQuery({
    queryKey: rolesPermissionsKeys.projectRoles(projectId!),
    queryFn: () => getProjectRoles(projectId!),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

export const useProjectMemberships = (projectId: number | undefined) =>
  useQuery({
    queryKey: rolesPermissionsKeys.projectMemberships(projectId!),
    queryFn: () => getProjectMemberships(projectId!),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

export const useProjectUsers = (projectId: number | undefined) =>
  useQuery({
    queryKey: rolesPermissionsKeys.projectUsers(projectId!),
    queryFn: () => getProjectUsers(projectId!),
    enabled: Boolean(projectId),
    staleTime: 60_000,
  });

export const useProjectAdministrators = (projectId: number | undefined) =>
  useQuery({
    queryKey: rolesPermissionsKeys.projectAdministrators(projectId!),
    queryFn: () => getProjectAdministrators(projectId!),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

export const useProjectPermissionRequests = (
  projectId: number | undefined,
  status?: string,
) =>
  useQuery({
    queryKey: rolesPermissionsKeys.projectPermissionRequests(projectId!, status),
    queryFn: () => getProjectPermissionRequests(projectId!, status),
    enabled: Boolean(projectId),
    staleTime: 15_000,
  });

export const usePermissionRequestMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  const invalidateRequests = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({
      queryKey: [...rolesPermissionsKeys.all, 'project-permission-requests', projectId],
    });
  };

  const createRequest = useMutation({
    mutationFn: (payload: PermissionRequestCreateInput) =>
      createProjectPermissionRequest(projectId!, payload),
    onSuccess: invalidateRequests,
  });

  const reviewRequest = useMutation({
    mutationFn: ({
      requestId,
      payload,
    }: {
      requestId: number;
      payload: PermissionRequestReviewInput;
    }) => reviewProjectPermissionRequest(projectId!, requestId, payload),
    onSuccess: invalidateRequests,
  });

  return { createRequest, reviewRequest };
};

export const useRolesMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const invalidateProjectData = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({ queryKey: rolesPermissionsKeys.projectRoles(projectId) });
    queryClient.invalidateQueries({ queryKey: rolesPermissionsKeys.projectMemberships(projectId) });
  };

  const createRole = useMutation({
    mutationFn: (payload: RoleInput) => createProjectRole(projectId!, payload),
    onSuccess: invalidateProjectData,
  });

  const updateRole = useMutation({
    mutationFn: ({ roleId, payload }: { roleId: number; payload: Partial<RoleInput> }) =>
      updateProjectRole(projectId!, roleId, payload),
    onSuccess: invalidateProjectData,
  });

  const deleteRole = useMutation({
    mutationFn: (roleId: number) => deleteProjectRole(projectId!, roleId),
    onSuccess: invalidateProjectData,
  });

  const upsertMembership = useMutation({
    mutationFn: (payload: MembershipInput) => upsertProjectMembership(projectId!, payload),
    onSuccess: invalidateProjectData,
  });

  const removeMembership = useMutation({
    mutationFn: (membershipId: number) => deleteProjectMembership(projectId!, membershipId),
    onSuccess: invalidateProjectData,
  });

  return { createRole, updateRole, deleteRole, upsertMembership, removeMembership };
};

export const useAdministratorsMutation = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AdministratorsUpdateInput) =>
      updateProjectAdministrators(projectId!, payload),
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: rolesPermissionsKeys.projectAdministrators(projectId),
        });
        queryClient.invalidateQueries({
          queryKey: rolesPermissionsKeys.projectMemberships(projectId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.clientAdminProjects(),
      });
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.myAdminProjects(),
      });
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.clientProjectMembers(),
      });
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.clientOrganizationMembers(),
      });
    },
  });
};

export const useDeactivateOrganizationMemberMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => deactivateClientOrganizationMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.clientOrganizationMembers(),
      });
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.clientProjectMembers(),
      });
    },
  });
};

export const useClientProjectMembershipMutation = () => {
  const queryClient = useQueryClient();

  const invalidateMembershipData = (projectId?: number) => {
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.projectMemberships(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: rolesPermissionsKeys.projectRoles(projectId),
      });
    }
    queryClient.invalidateQueries({
      queryKey: rolesPermissionsKeys.clientProjectMembers(),
    });
    queryClient.invalidateQueries({
      queryKey: rolesPermissionsKeys.clientOrganizationMembers(),
    });
  };

  const changeRole = useMutation({
    mutationFn: ({
      projectId,
      membershipId,
      payload,
    }: {
      projectId: number;
      membershipId: number;
      payload: MembershipUpdateInput;
    }) => updateProjectMembership(projectId, membershipId, payload),
    onSuccess: (_data, variables) => invalidateMembershipData(variables.projectId),
  });

  const removeMembership = useMutation({
    mutationFn: ({
      projectId,
      membershipId,
    }: {
      projectId: number;
      membershipId: number;
    }) => deleteProjectMembership(projectId, membershipId),
    onSuccess: (_data, variables) => invalidateMembershipData(variables.projectId),
  });

  return { changeRole, removeMembership };
};
